import { glossaryService, GlossaryEntry } from './glossaryService';
import { v4 as uuidv4 } from 'uuid';

export interface ExtractedKeyword {
  term: string;
  definition: string;
  category: string;
  context: string;
  confidence: number;
}

export interface KeywordExtractionResult {
  keywords: ExtractedKeyword[];
  addedToGlossary: number;
  skipped: number;
  errors: string[];
}

class AIGlossaryService {
  private readonly DEFAULT_CATEGORY = 'AI Generated';
  private readonly MIN_CONFIDENCE = 0.7;

  /**
   * Extract keywords from tutorial content using AI
   */
  async extractKeywordsFromContent(content: string, tutorialTitle: string): Promise<ExtractedKeyword[]> {
    // This would typically call an AI service to extract keywords
    // For now, we'll use a simple pattern-based approach
    const keywords: ExtractedKeyword[] = [];
    
    // Common technical terms that might appear in tutorials
    const technicalTerms = [
      'fiber optic', 'cable', 'connector', 'MPO', 'LC', 'MMC', 'jumper', 'trunk',
      'core', 'cladding', 'jacket', 'polarity', 'insertion loss', 'return loss',
      'UPC', 'APC', 'plenum', 'riser', 'key up', 'key down', 'ferrule', 'boot'
    ];

    const contentLower = content.toLowerCase();
    
    for (const term of technicalTerms) {
      if (contentLower.includes(term.toLowerCase())) {
        const context = this.extractContext(content, term);
        const definition = this.generateDefinition(term, context);
        
        keywords.push({
          term: term,
          definition: definition,
          category: this.getCategoryForTerm(term),
          context: context,
          confidence: 0.8
        });
      }
    }

    return keywords;
  }

  /**
   * Add extracted keywords to the glossary
   */
  async addKeywordsToGlossary(keywords: ExtractedKeyword[]): Promise<KeywordExtractionResult> {
    const result: KeywordExtractionResult = {
      keywords: keywords,
      addedToGlossary: 0,
      skipped: 0,
      errors: []
    };

    for (const keyword of keywords) {
      try {
        // Check if term already exists
        const existingEntries = await glossaryService.getAllEntries();
        const existingEntry = existingEntries.find(entry => 
          entry.term.toLowerCase() === keyword.term.toLowerCase()
        );

        if (existingEntry) {
          result.skipped++;
          continue;
        }

        // Add to glossary
        const newEntry = glossaryService.addEntry({
          term: keyword.term,
          definition: keyword.definition,
          category: keyword.category,
          tags: ['ai-generated', 'tutorial'],
          isActive: true
        });

        if (newEntry) {
          result.addedToGlossary++;
        }

      } catch (error) {
        result.errors.push(`Failed to add keyword "${keyword.term}": ${error}`);
      }
    }

    return result;
  }

  /**
   * Process tutorial content and add keywords to glossary
   */
  async processTutorialForGlossary(tutorial: {
    title: string;
    description: string;
    steps: Array<{ title: string; content: string; }>;
  }): Promise<KeywordExtractionResult> {
    // Combine all content
    const allContent = [
      tutorial.title,
      tutorial.description,
      ...tutorial.steps.map(step => `${step.title} ${step.content}`)
    ].join(' ');

    // Extract keywords
    const keywords = await this.extractKeywordsFromContent(allContent, tutorial.title);

    // Add to glossary
    const result = await this.addKeywordsToGlossary(keywords);

    return result;
  }

  private extractContext(content: string, term: string): string {
    const index = content.toLowerCase().indexOf(term.toLowerCase());
    if (index === -1) return '';

    const start = Math.max(0, index - 50);
    const end = Math.min(content.length, index + term.length + 50);
    
    return content.substring(start, end).trim();
  }

  private generateDefinition(term: string, context: string): string {
    // Simple definition generation based on term
    const definitions: Record<string, string> = {
      'fiber optic': 'A technology that uses light to transmit data through thin glass or plastic fibers.',
      'cable': 'A protective covering containing one or more conductors for transmitting electrical or optical signals.',
      'connector': 'A device used to join electrical or optical circuits together.',
      'MPO': 'Multi-fiber Push-On connector, a high-density fiber optic connector.',
      'LC': 'Lucent Connector, a small form factor fiber optic connector.',
      'MMC': 'Multi-fiber Multi-channel connector, a high-density fiber optic connector.',
      'jumper': 'A short cable used to connect equipment within a rack or cabinet.',
      'trunk': 'A high-capacity cable containing many individual fibers.',
      'core': 'The central part of an optical fiber where light travels.',
      'cladding': 'The layer surrounding the core that reflects light back into the core.',
      'jacket': 'The outer protective covering of a cable.',
      'polarity': 'The arrangement of fibers in a multi-fiber connector to ensure proper signal routing.',
      'insertion loss': 'The amount of optical power lost when light passes through a connection.',
      'return loss': 'The amount of light reflected back toward the source.',
      'UPC': 'Ultra Physical Contact, a type of fiber connector polish.',
      'APC': 'Angled Physical Contact, a type of fiber connector polish with an angled end face.',
      'plenum': 'A space used for air circulation, requiring special fire-resistant cables.',
      'riser': 'A vertical space between floors, requiring specific cable ratings.',
      'key up': 'MPO connector orientation with the key facing upward.',
      'key down': 'MPO connector orientation with the key facing downward.',
      'ferrule': 'A precision component that holds and aligns optical fibers.',
      'boot': 'A protective sleeve that provides strain relief at cable connections.'
    };

    return definitions[term.toLowerCase()] || `Technical term related to ${term}.`;
  }

  private getCategoryForTerm(term: string): string {
    const categories: Record<string, string> = {
      'fiber optic': 'Fiber Optics',
      'cable': 'Cables',
      'connector': 'Connectors',
      'MPO': 'Connectors',
      'LC': 'Connectors',
      'MMC': 'Connectors',
      'jumper': 'Cables',
      'trunk': 'Cables',
      'core': 'Fiber Optics',
      'cladding': 'Fiber Optics',
      'jacket': 'Cables',
      'polarity': 'Fiber Optics',
      'insertion loss': 'Performance',
      'return loss': 'Performance',
      'UPC': 'Connectors',
      'APC': 'Connectors',
      'plenum': 'Cables',
      'riser': 'Cables',
      'key up': 'Connectors',
      'key down': 'Connectors',
      'ferrule': 'Connectors',
      'boot': 'Connectors'
    };

    return categories[term.toLowerCase()] || this.DEFAULT_CATEGORY;
  }
}

export const aiGlossaryService = new AIGlossaryService();
export default aiGlossaryService;
